// scheduling/send-protocol.ts
import { EngineToolError, type EngineErrorCode } from '../interfaces/whatsapp-tools-commands.interface'
import { failureToMetadata, sanitizeFailure, type SendFailEngine, type SendFailure } from '../engine/send-failure'
import type {
    SendProtocolDeps,
    SendProtocolInput,
    SendProtocolResult,
    AttemptType,
    SendFnResult,
    SendProtocolStore,
    StoreCheckHit,
} from './send-protocol.types'
import { ACK } from '../schemas/wa/message-ack'
import { resolveWireSecret } from '../utils/send-prep'
import { isVersionAtLeast } from '../notifier/version-gate'

// wa-js Message.ack the store-check must clear before a stored copy counts as a
// real, confirmed send: -1 ERROR, 0 PENDING, 1 SERVER (✓ — WhatsApp's server got
// it), 2 DELIVERED (✓✓), 3 READ. A store entry at ack < ACK.SERVER (PENDING/ERROR)
// never reached WhatsApp, so the protocol must treat it as NOT FOUND (still send /
// stay retry-eligible) instead of falsely confirming it.

/** The §1.4 record's engine for a `clientType`. The pod is `baileys-pod`; the extension (`whatsapp`, `frontend`, the
 *  legacy `extension-content`) and the Chrome gateway (`gateway`) all run the D0 native path. Anything else is
 *  `unknown` (the sanitizer's own fallback), never guessed. */
function engineOfClientType(clientType: string): SendFailEngine {
    switch (clientType) {
        case 'baileys-pod': return 'baileys-pod'
        case 'whatsapp':
        case 'frontend':
        case 'gateway':
        case 'extension-content': return 'wa-web-native'
        default: return 'unknown'
    }
}

/** FE#360 — the WhatsApp Status ("Story") broadcast JID. A scheduled message
 *  addressed to it is a Status post, not a chat send. */
const STATUS_BROADCAST_TARGET = 'status@broadcast'

/** FE#360 — is this send a Status post? */
function isStatusTarget(chatId: string | undefined | null): boolean {
    return String(chatId ?? '').trim() === STATUS_BROADCAST_TARGET
}

/**
 * FE#360 — does a store-check hit prove a prior delivery?
 *
 * For a chat send: only at ack >= SERVER. A copy at PENDING/ERROR never reached
 * WhatsApp, so it must stay retry-eligible rather than be falsely confirmed.
 *
 * For a Status post there IS no per-recipient ack — the whole reason FE#335 calls
 * it a one-shot — and the posted model is seeded at ACK.CLOCK (0). Applying the
 * chat-send ack gate to a Status therefore rejects EVERY hit, so the guard falls
 * through and re-posts: a second public Status that cannot be un-posted. For a
 * Status the tag hit alone is the proof, because the tag is only ever stamped by
 * the engine that actually posted it.
 */
function storeHitProvesDelivery(hit: StoreCheckHit, chatId: string | undefined | null): boolean {
    if (isStatusTarget(chatId)) return true
    return hit.ack >= ACK.SERVER
}

/**
 * #385 — how much a store-check hit proves about delivery.
 *  - `delivered`:  proven (ack >= SERVER, or a Status tag hit) → confirm, never re-send.
 *  - `maybe-sent`: the engine says the copy exists only because the stanza went out, and no ack has
 *                  been seen (PENDING). Re-sending risks a duplicate → defer, never re-send.
 *  - `unsent`:     the copy never reached WhatsApp (outbox PENDING without the flag, or ERROR) →
 *                  retry-eligible, send again.
 */
export type StoreHitVerdict = 'delivered' | 'maybe-sent' | 'unsent'

export function classifyStoreHit(hit: StoreCheckHit, chatId: string | undefined | null): StoreHitVerdict {
    if (storeHitProvesDelivery(hit, chatId)) return 'delivered'
    return hit.onWire === true && hit.ack === ACK.PENDING ? 'maybe-sent' : 'unsent'
}

// Bound on the failure-path store-at-failure probe. The probe's likeliest
// target state is a wedged page whose store IPC also hangs — unbounded, it
// would delay the failure log write (and the serialized campaign pacer) by up
// to the bus ceiling. The store read itself is fast when the page is healthy.
const STORE_PROBE_TIMEOUT_MS = 1500

/**
 * Strip engine diagnostic suffixes (`| send-trace: …`, `| store-at-failure: …`)
 * from an error string. The customer-visible channels — the usermessage's
 * sendFailureReason (rendered verbatim in the UI and exposed via /v1) and this
 * protocol's `result.error` — must carry only the raw failure reason: the
 * suffixes are internal forensics (they live in messagelog metadata), they
 * break exact-match consumers of failure reasons, and a suffix on an EMPTY
 * error would flip the backend's sent/failed status decision.
 */
export function stripDiagnosticSuffixes(error: string): string {
    return error.split(/ \| (?:send-trace|store-at-failure|media-diag): /)[0]
}

/** The diagnostic suffixes carried on an engine error string, split apart. */
export interface SplitDiagnostics {
    /** The error with every diagnostic suffix removed — identical to `stripDiagnosticSuffixes`. */
    base: string
    /** Everything after ` | send-trace: `, up to the next suffix. Undefined when absent. */
    sendTrace?: string
    /** Everything after ` | store-at-failure: `, up to the next suffix. Undefined when absent. */
    storeAtFailure?: string
}

/**
 * Split an engine error into its base reason and its diagnostic suffixes.
 *
 * #94: the suffixes used to ride inside `sendErrorReason`, which is capped at
 * SEND_ERROR_REASON_MAX. `send-trace` is appended LAST, so the cap ate it — measured
 * 2026-08-02, all 90 instrumented media failures were clipped at exactly 300 characters,
 * truncating mid-token precisely where the trace describes what happened after a successful
 * upload. Splitting lets each part be stored (and bounded) on its own field.
 *
 * Deliberately shares the suffix vocabulary with `stripDiagnosticSuffixes` above — one
 * delimiter set, so a new suffix cannot be stripped from the error but silently dropped here.
 * `media-diag` is intentionally NOT captured: it is already persisted as structured fields
 * (mediaFailSite / mediaUploadStage / mediaUploadResultKind / mediaMimetype / mediaSizeBytes).
 */
export function splitDiagnosticSuffixes(error: string): SplitDiagnostics {
    const grab = (label: 'send-trace' | 'store-at-failure'): string | undefined => {
        // Capture up to the next ` | <suffix>: ` boundary (or end of string), so suffix order
        // never matters — a trace followed by store-at-failure yields the trace alone.
        const m = error.match(
            new RegExp(` \\| ${label}: (.*?)(?= \\| (?:send-trace|store-at-failure|media-diag): |$)`, 's'),
        )
        const v = m?.[1]?.trim()
        return v ? v : undefined
    }
    return {
        base: stripDiagnosticSuffixes(error),
        sendTrace: grab('send-trace'),
        storeAtFailure: grab('store-at-failure'),
    }
}

/**
 * Slot verdicts that mean the message is DEAD, not merely waiting: it is terminal, aged out of its
 * send window, or gone from the database entirely. No later attempt can succeed.
 *
 * The client's own pacer keeps re-offering such a message until its local copy catches up, and each
 * offer used to write an attempt-log row — 394 rows in ~30 minutes against ONE dead message in the
 * filed case, on a ~54GB collection where lock-denied noise has previously measured ~20% of rows.
 * The exchange is correct; recording it is waste. (#172)
 */
const DEAD_SLOT_REASONS: ReadonlySet<string> = new Set(['already_terminal', 'window_expired', 'not_found'])

/** The exact sentences the legacy has-send-attempt path has always written. Preserved byte-for-byte:
 *  the backend's slot-denial detector recognises un-drained engines by this prefix, and that path has
 *  no slot verdict to report in their place. */
const LEGACY_LOCK_DENIED_STORE_MISS =
    'Lock denied — another engine holds the lock but the message is NOT visible in this engine\'s store. Skipped to avoid double-send; check whether the lock-holder completed its write-back.'
const LEGACY_LOCK_DENIED_NO_STORE =
    'Lock denied — no store adapter available to verify whether the message was already sent (engine misconfigured). Skipped to avoid double-send.'

/** Denial log text. With a slot verdict in hand, say what actually happened instead of asserting a
 *  contested lock that may not exist — the filed customer had no second engine at all. */
function slotDenialLogMessage(slotReason: string | undefined, verdict: 'store_miss' | 'no_store_adapter'): string {
    if (!slotReason) {
        return verdict === 'no_store_adapter' ? LEGACY_LOCK_DENIED_NO_STORE : LEGACY_LOCK_DENIED_STORE_MISS
    }
    return verdict === 'no_store_adapter'
        ? `Send slot denied (${slotReason}) — no store adapter available to verify whether the message was already sent (engine misconfigured). Skipped to avoid double-send.`
        : `Send slot denied (${slotReason}) — the message is NOT visible in this engine's store, so nothing was sent.`
}

function preAttemptLogMessage(attemptType: AttemptType, retryAttemptNumber: number): string {
    if (attemptType === 'initial') return 'Preparing initial send attempt'
    return `Preparing retry attempt #${retryAttemptNumber}`
}

function successLogMessage(attemptType: AttemptType, retryAttemptNumber: number): string {
    if (attemptType === 'initial') return 'Initial send sent successfully via WhatsApp'
    return `Retry #${retryAttemptNumber} sent successfully via WhatsApp`
}

// The server queue re-offers a declined message to the same too-old engine every cycle (~12/min),
// so the decline line is emitted once per message id. Process-local and bounded (insertion-order
// eviction): an evicted id just logs once more, and the decline itself is never skipped.
const DECLINED_LOGGED_MAX = 50_000 // ids are ~25 chars; must exceed any queue backlog or a cyclic re-offer thrashes the set
const declinedInFlight = new Set<string>()
const declinedLogged = new Set<string>()

function rememberDecline(messageId: string): void {
    if (declinedLogged.size >= DECLINED_LOGGED_MAX) {
        declinedLogged.delete(declinedLogged.values().next().value as string)
    }
    declinedLogged.add(messageId)
}

/** Test seam: forget remembered declines. */
export function _resetDeclinedMessagesForTests(): void {
    declinedLogged.clear()
    declinedInFlight.clear()
}

export async function runSendProtocol(
    input: SendProtocolInput,
    deps: SendProtocolDeps,
): Promise<SendProtocolResult> {
    const { backendClient, sendFn, engineContext, log } = deps
    const { userMessage, whatsappStatus } = input

    // Min-engine-version gate (server-declares / client-self-rejects). Runs FIRST —
    // before the lock/slot is touched — so a too-old engine consumes no send attempt
    // and simply declines, leaving the message pending for a qualified engine / a
    // later attempt after this engine updates. Absent `minEngineVersion` ⇒ no gate,
    // so every ordinary message is unaffected (backward compatible). Fail-safe: an
    // unknown/unparseable own-version never satisfies the requirement.
    if (userMessage.minEngineVersion &&
        !isVersionAtLeast(engineContext.extensionVersion, userMessage.minEngineVersion)) {
        // H24: leave a trace on the message. A silent decline looked like "nobody tried" — and a pod that
        // lacks ENGINE_VERSION declines EVERY gated message. Bookkeeping only: best-effort, never touches
        // the lock/slot, and a failed write cannot change the decline.
        // Once per message id: the queue re-offers it every cycle (~12/min), which would otherwise write
        // one messagelogs row and one log line per offer. The id is remembered only after the row lands,
        // so a failed write is retried on the next offer.
        if (!declinedLogged.has(userMessage._id) && !declinedInFlight.has(userMessage._id)) {
            declinedInFlight.add(userMessage._id)
            try {
                log?.('info',
                    `[send-protocol] declining message ${userMessage._id}: engine version ` +
                    `${engineContext.extensionVersion ?? 'unknown'} < required minEngineVersion ` +
                    `${userMessage.minEngineVersion} — leaving for a qualified engine`)
                const written = await backendClient.createMessageAttemptLog({
                    messageId: userMessage._id,
                    attemptType: input.attemptType,
                    whatsappStatus,
                    logMessage: `Declined by engine: version ${engineContext.extensionVersion ?? 'unknown'} is below the required ${userMessage.minEngineVersion}`,
                    mqttClientId: engineContext.mqttClientId,
                    clientType: engineContext.clientType,
                    waVersion: engineContext.waVersion,
                    waMeUser: engineContext.waMeUser,
                })
                // The production client reports failure as {success:false}; it does not throw.
                if (written && written.success === false) throw new Error(written.error ?? 'row write failed')
                rememberDecline(userMessage._id)
            } catch (err: unknown) {
                log?.('warn', `[send-protocol] min-version decline log failed for ${userMessage._id}: ${err instanceof Error ? err.message : 'unknown'}`)
            } finally {
                declinedInFlight.delete(userMessage._id)
            }
        }
        return { status: 'aborted', reason: 'engine_below_min_version' }
    }
    // Store-check ("already sent?"). The stored copy is matched by the wire token,
    // and only the `_id` token is unique, so only it can prove THIS message was
    // delivered. When the token is the caller's `secret` (resolveWireSecret) the
    // check reports a miss and the can-i-send lock alone guards the send — never
    // a silent skip of a different message that reused the secret.
    const idIsWireToken = resolveWireSecret(userMessage) === userMessage._id
    const findStoredCopy = (store: SendProtocolStore, chatId: string) => idIsWireToken
        ? store.findUserMessageKey({ chatId, userMessageId: userMessage._id })
        : null
    // attemptType + retryAttemptNumber are MUTABLE: on the server-authoritative slot path the SERVER
    // supplies the attempt#, overriding whatever the engine passed in (the engine is a dumb sender).
    let attemptType = input.attemptType
    let retryAttemptNumber = input.retryAttemptNumber
    let allowToSend: boolean
    let lockUserMessage: typeof userMessage = userMessage
    // The server's verdict for this attempt, carried onto every log row it produces. Stays undefined
    // on the legacy has-send-attempt path, which has no verdict to report.
    let slotReason: string | undefined

    const writeAbortLog = (): ReturnType<typeof backendClient.createMessageAttemptLog> => backendClient.createMessageAttemptLog({
        messageId: userMessage._id,
        attemptType,
        whatsappStatus,
        logMessage: `Failed to validate ${attemptType === 'initial' ? 'initial send attempt' : `retry attempt #${retryAttemptNumber}`}`,
        mqttClientId: engineContext.mqttClientId,
        clientType: engineContext.clientType,
        waVersion: engineContext.waVersion,
        waMeUser: engineContext.waMeUser,
        waState: input.waState,
        waStream: input.waStream,
        waHasSynced: input.waHasSynced,
    })

    // Version-based split (NOT a runtime flag): the newest engine build bundles the bt-common that
    // wires acquireSendSlot, so it always takes the server-authoritative path. Older builds bundle an
    // older bt-common without it and keep calling the legacy has-send-attempt endpoint — which the
    // server keeps alive purely for those un-updated versions until the fleet drains.
    if (backendClient.acquireSendSlot) {
        // Phase-2 server-authoritative path: the server advances the attempt#, enforces budget +
        // backoff, and acquires the monotonic lock. The engine no longer supplies retryAttemptNumber,
        // so the #132 client-freeze (recover-as-initial → re-request attempt 0 → deadlock) is gone.
        const slot = await backendClient.acquireSendSlot({
            messageId: userMessage._id,
            isSendMessageNow: !!input.isSendMessageNow,
            clientType: engineContext.clientType,
            extensionVersion: engineContext.extensionVersion,
            // Per-message send metadata (group/community, message-type, addressing) carried on the
            // engineContext — mirrors extensionVersion. Persistence onto messagelogs.metadata happens
            // engine-side at the HTTP-body-stamping boundary; here it only enriches the send-slot marker.
            isGroup: engineContext.isGroup,
            isCommunity: engineContext.isCommunity,
            isParentGroup: engineContext.isParentGroup,
            announce: engineContext.announce,
            groupType: engineContext.groupType,
            parentGroupId: engineContext.parentGroupId,
            messageType: engineContext.messageType,
            sendErrorReason: engineContext.sendErrorReason,
            recipientAddressing: engineContext.recipientAddressing,
        })
        if (!slot) {
            await writeAbortLog()
            return { status: 'aborted', reason: 'slot_check_failed' }
        }
        allowToSend = slot.allowToSend
        slotReason = slot.reason
        retryAttemptNumber = slot.attemptNumber
        attemptType = retryAttemptNumber === 0 ? 'initial' : 'scheduler-retry'
        lockUserMessage = slot.userMessage ?? userMessage
        if (!allowToSend && slot.reason === 'gateway_preferred') {
            // Expected wait, not a contested lock: the server is giving the connected offline
            // gateway first shot during the initial dueDate grace window (BE#167). No store-check
            // and no attempt-log — the pacer will simply re-attempt on its next tick, and the
            // server burns no attempt/backoff state on this deny, so the post-grace re-attempt
            // is a clean attempt-0 grant if the gateway never sent.
            return { status: 'lock_denied', reason: 'gateway_preferred' }
        }
        if (!allowToSend && slot.reason === 'not_yet_due') {
            // BE#657: the doc isn't due yet (future dueDate, not a Send Now) — a DEFERRAL, not a
            // contested lock. Same handling as gateway_preferred: no store-check, no attempt-log
            // row (so a hot-looping engine doesn't spam messagelogs, and deferrals are never
            // mislabelled as 'backoff'). The server has re-synced the fresh doc to the owner, so
            // the engine's cache is corrected and the pacer stops offering it early.
            return slot.retryAfterMs !== undefined
                ? { status: 'lock_denied', reason: 'not_yet_due', retryAfterMs: slot.retryAfterMs }
                : { status: 'lock_denied', reason: 'not_yet_due' }
        }
    } else {
        // Legacy path: the engine supplies retryAttemptNumber (client-owned attempt#).
        const lock = await backendClient.hasSendAttemptBeenMadeToMessage({
            messageId: userMessage._id,
            retryAttemptNumber,
            isSendMessageNow: !!input.isSendMessageNow,
        })
        if (!lock) {
            await writeAbortLog()
            return { status: 'aborted', reason: 'lock_check_failed' }
        }
        allowToSend = lock.allowToSend
        lockUserMessage = lock.userMessage ?? userMessage
    }

    // #385 — record a MAYBE-SENT copy (post-wire, ack not seen) as sent-pending-ack: the success write
    // with the copy's key and ack 0 resolves to `sent_pending_ack` (send-result-status), so the message
    // is neither re-sent nor marked failed, never burns another attempt, and the pending-ack
    // reconciler / abandoned reaper own it from here.
    const recordMaybeSent = (hit: StoreCheckHit): Promise<Awaited<ReturnType<typeof backendClient.deleteUserMessageAndCreateLog>>> =>
        backendClient.deleteUserMessageAndCreateLog({
            messageId: userMessage._id,
            waMessageKey: hit.waMessageKey,
            ack: hit.ack,
            sendFailureError: undefined,
            attemptType,
            whatsappStatus,
            logMessage: `${successLogMessage(attemptType, retryAttemptNumber)} — maybe-sent: stanza went out, ack not seen (store: present ack=${hit.ack}); recorded pending-ack, not re-sent (#385)`,
            retryTrigger: input.retryTrigger,
            mqttClientId: engineContext.mqttClientId,
            clientType: engineContext.clientType,
            waVersion: engineContext.waVersion,
            waMeUser: engineContext.waMeUser,
            waState: input.waState,
            waStream: input.waStream,
            waHasSynced: input.waHasSynced,
        })

    if (!allowToSend) {
        const chatId = userMessage.contactList?.[0]?.id
        const writeLockDeniedLog = (verdict: 'store_miss' | 'no_store_adapter'): Promise<unknown> => {
            // A dead message's denials are noise, not history — write nothing. The store-check above
            // still runs, so a message that WAS delivered takes the delete-and-log reconciliation
            // path instead; only the "denied and genuinely not sent" row is dropped. (#172)
            if (slotReason && DEAD_SLOT_REASONS.has(slotReason)) {
                log?.('debug',
                    `[send-protocol] slot denied ${slotReason} for ${userMessage._id} — message is dead, skipping the attempt-log row`)
                return Promise.resolve()
            }
            return backendClient.createMessageAttemptLog({
                messageId: userMessage._id,
                attemptType,
                whatsappStatus,
                logMessage: slotDenialLogMessage(slotReason, verdict),
                slotReason,
                retryTrigger: input.retryTrigger,
                mqttClientId: engineContext.mqttClientId,
                clientType: engineContext.clientType,
                waVersion: engineContext.waVersion,
                waMeUser: engineContext.waMeUser,
                waState: input.waState,
                waStream: input.waStream,
                waHasSynced: input.waHasSynced,
            }).catch((err: unknown) => {
                log?.('warn', `[send-protocol] lock-denied attempt-log write failed: ${err instanceof Error ? err.message : 'unknown'}`)
            })
        }
        if (!deps.store || !chatId) {
            await writeLockDeniedLog('no_store_adapter')
            return { status: 'lock_denied', reason: 'no_store_adapter' }
        }
        const existing = await findStoredCopy(deps.store, chatId)
        // ack-gate: a stored copy at ack < SERVER (PENDING/ERROR) never reached
        // WhatsApp, so it is NOT a confirmed send — treat it like a store miss and
        // return lock_denied so the message stays retry-eligible (not falsely
        // confirmed-and-deleted). Only ack >= SERVER confirms — EXCEPT a Status
        // post, which has no ack at all; see storeHitProvesDelivery.
        if (existing && classifyStoreHit(existing, chatId) === 'maybe-sent') {
            const logResponse = await recordMaybeSent(existing)
            return { status: 'skipped_already_sent', waMessageKey: existing.waMessageKey, logResponse }
        }
        if (!existing || !storeHitProvesDelivery(existing, chatId)) {
            await writeLockDeniedLog('store_miss')
            return slotReason
                ? { status: 'lock_denied', reason: 'store_miss', slotReason }
                : { status: 'lock_denied', reason: 'store_miss' }
        }
        const logResponse = await backendClient.deleteUserMessageAndCreateLog({
            messageId: userMessage._id,
            waMessageKey: existing.waMessageKey,
            // BE#209: forward the OBSERVED ack (proven >= ACK.SERVER by the gate
            // above), so the persisted record carries the real delivery level
            // instead of being confirmed-but-ackless. Mirrors the happy path.
            ack: existing.ack,
            sendFailureError: undefined,
            attemptType,
            whatsappStatus,
            logMessage: 'Message already sent successfully — store-check confirmed key',
            retryTrigger: input.retryTrigger,
            mqttClientId: engineContext.mqttClientId,
            clientType: engineContext.clientType,
            waVersion: engineContext.waVersion,
            waMeUser: engineContext.waMeUser,
            waState: input.waState,
            waStream: input.waStream,
            waHasSynced: input.waHasSynced,
        })
        return { status: 'skipped_already_sent', waMessageKey: existing.waMessageKey, logResponse }
    }

    // Happy path — lock allows send.
    //
    // Granted-path store-check (closes the lock-TTL-expiry duplicate-send hole).
    // The Redis lock has a 3600s TTL; once it expires it re-grants, so a message
    // already delivered by a prior attempt — possibly from a DIFFERENT engine —
    // can reach this granted path again. WhatsApp multi-device sync means that
    // prior send is visible in THIS engine's store, so we check by the unique
    // userMessage._id tag (addressing-agnostic; see findStoredCopy) BEFORE sending. A hit means the
    // message was already delivered → skip instead of double-sending. Mirrors the
    // lock-denied store-check; both engines wire their own `store` adapter.
    const grantedChatId = userMessage.contactList?.[0]?.id
    if (deps.store && grantedChatId) {
        const alreadySent = await findStoredCopy(deps.store, grantedChatId)
        // ack-gate: only a stored copy at ack >= SERVER counts as already-delivered
        // and skips the send. A copy at ack < SERVER (PENDING/ERROR) never reached
        // WhatsApp, so fall through and (re-)send it rather than falsely confirming.
        // A Status post is the documented exception (no ack exists) —
        // storeHitProvesDelivery takes the tag hit itself as the proof.
        if (alreadySent && classifyStoreHit(alreadySent, grantedChatId) === 'maybe-sent') {
            // #385 — a post-wire copy with no ack yet: record it pending-ack instead of re-sending a duplicate.
            const logResponse = await recordMaybeSent(alreadySent)
            return { status: 'skipped_already_sent', waMessageKey: alreadySent.waMessageKey, logResponse }
        }
        if (alreadySent && storeHitProvesDelivery(alreadySent, grantedChatId)) {
            const logResponse = await backendClient.deleteUserMessageAndCreateLog({
                messageId: userMessage._id,
                waMessageKey: alreadySent.waMessageKey,
                // BE#209: forward the OBSERVED ack (proven >= ACK.SERVER by the
                // guard condition), so a TTL-re-grant store-check confirmation is
                // persisted WITH its real delivery level, not ackless.
                ack: alreadySent.ack,
                sendFailureError: undefined,
                attemptType,
                whatsappStatus,
                logMessage: 'Message already sent successfully — store-check confirmed key (granted-path guard)',
                retryTrigger: input.retryTrigger,
                mqttClientId: engineContext.mqttClientId,
                clientType: engineContext.clientType,
                waVersion: engineContext.waVersion,
                waMeUser: engineContext.waMeUser,
                waState: input.waState,
                waStream: input.waStream,
                waHasSynced: input.waHasSynced,
            })
            return { status: 'skipped_already_sent', waMessageKey: alreadySent.waMessageKey, logResponse }
        }
    }

    await backendClient.createMessageAttemptLog({
        messageId: userMessage._id,
        attemptType,
        whatsappStatus,
        logMessage: preAttemptLogMessage(attemptType, retryAttemptNumber),
        // 'granted' on the happy path, so a row's slot verdict is present whether the slot allowed
        // the send or refused it — the backend's denial detector keys on `slotReason !== 'granted'`.
        slotReason,
        mqttClientId: engineContext.mqttClientId,
        clientType: engineContext.clientType,
        waVersion: engineContext.waVersion,
        waMeUser: engineContext.waMeUser,
        waState: input.waState,
        waStream: input.waStream,
        waHasSynced: input.waHasSynced,
    })

    let threwMessage: string | undefined
    let caughtError: unknown
    let sentOk = false
    let sentKey = ''
    let sentAck: number | undefined
    let failureError = ''
    let failureCode: EngineErrorCode = 'engine_error'
    let retryAfterSec: number | undefined
    // The engine's own §1.4 record, when the throw site built one (returned or thrown).
    let engineFailure: SendFailure | undefined
    try {
        const gate = await recipientGate(deps, lockUserMessage.contactList?.[0]?.id, engineOfClientType(engineContext.clientType))
        const sr = gate ?? await sendFn({
            userMessage: lockUserMessage,
            retryAttemptNumber,
            attemptType,
        })
        if (sr.ok === true) {
            sentOk = true
            sentKey = sr.waMessageKey
            sentAck = sr.ack
        } else {
            failureError = sr.error
            // Fail-safe against a double send: a failure without a code may have
            // reached WhatsApp, so it is ambiguous (the store probe decides).
            failureCode = sr.code ?? 'ambiguous'
            retryAfterSec = sr.retryAfterSec
            engineFailure = sr.failure
        }
    } catch (err: unknown) {
        caughtError = err
        threwMessage = err instanceof Error ? err.message : String(err)
        failureError = threwMessage
        // A thrown EngineToolError carries its code; any other throw may have
        // reached WhatsApp → ambiguous.
        failureCode = err instanceof EngineToolError ? err.code : 'ambiguous'
        retryAfterSec = err instanceof EngineToolError ? err.retryAfterSec : undefined
        engineFailure = err instanceof EngineToolError ? err.failure : undefined
    }

    if (sentOk) {
        const logResponse = await backendClient.deleteUserMessageAndCreateLog({
            messageId: userMessage._id,
            waMessageKey: sentKey,
            ack: sentAck,
            sendFailureError: undefined,
            attemptType,
            whatsappStatus,
            logMessage: successLogMessage(attemptType, retryAttemptNumber),
            retryTrigger: input.retryTrigger,
            mqttClientId: engineContext.mqttClientId,
            clientType: engineContext.clientType,
            waVersion: engineContext.waVersion,
            waMeUser: engineContext.waMeUser,
            waState: input.waState,
            waStream: input.waStream,
            waHasSynced: input.waHasSynced,
        })
        log?.('info', `[send-protocol] sent ${userMessage._id} as ${sentKey}`)
        return { status: 'sent', waMessageKey: sentKey, logResponse }
    }

    // F1 — rate-backoff DEFERRAL, not a failure. When the engine's send-time
    // anti-spam gate declines because a server backoff is active (WhatsApp NACK
    // or a 402 temp-ban), the send never hit the socket: the message was NOT
    // rejected by WhatsApp, it was intentionally held. Treat it exactly like a
    // lock-denied deferral — write NO failure log (so it never flips to `failed`
    // / burns the failure-log retryCount / dirties campaign counters and /v1) and
    // leave the message pending + retry-eligible. The server slot's fixed
    // inter-attempt backoff (acquire-send-slot) throttles the re-dispatch, so the
    // wait is respected without a spurious failure row. The marker is emitted by
    // the engine's sendFn as code `rate_limited`; a genuine WhatsApp rejection
    // carries its own code and falls through to the failure path below.
    if (failureCode === 'rate_limited') {
        log?.('warn', `[send-protocol] rate_limited deferral for ${userMessage._id} — held, no failure log, retry-eligible`)
        return { status: 'lock_denied', reason: 'rate_limited', retryAfterSec }
    }

    // Failure path — write the failure log row so campaign counters know the
    // attempt happened. waMessageKey is empty; sendFailureError carries the
    // reason.
    //
    // Store-at-failure snapshot (observability): a client-side timeout
    // (`Timeout in sendMessageViaWhatsapp after 50000 ms`) is a Promise.race
    // that stops waiting — it does NOT cancel or prove the underlying send
    // failed. The message may have actually reached WhatsApp (slow ack) or the
    // send may have truly hung. Probe the ack-gated store here so the failure
    // row self-classifies: `present ack>=1` = false-negative (delivered despite
    // the error), `absent` = genuine loss (as far as this probe can tell).
    //
    // Containment rules (review findings):
    //  - Probe ONLY ambiguous failures (code `ambiguous`: timeouts, thrown send errors). A
    //    deterministic engine verdict (e.g. NUMBER_INVALID) means the send
    //    provably never happened — probing would waste a store scan per
    //    failure (a full-RAM scan on the Baileys pod) for a guaranteed
    //    'absent'.
    //  - BOUND the probe. The likeliest probed state is a wedged page whose
    //    store IPC also hangs (up to a 120s bus ceiling on the extension);
    //    unbounded, it would stall the failure log write and the serialized
    //    campaign pacer. On expiry the verdict is 'lookup-timeout' — honest
    //    about indeterminacy rather than mislabeling as 'absent'.
    //  - The verdict rides the internal-only logMessage (metadata.logMessage),
    //    NEVER sendFailureError/result.error: those persist onto the
    //    usermessage doc, render verbatim in customer UI and the /v1 API, feed
    //    exact-match consumers, and their truthiness decides the backend
    //    sent/failed status.
    const isAmbiguousFailure = failureCode === 'ambiguous'
    let storeAtFailure = ''
    const failChatId = userMessage.contactList?.[0]?.id
    if (deps.store && failChatId && isAmbiguousFailure) {
        let hit: StoreCheckHit | null = null
        try {
            hit = await Promise.race([
                findStoredCopy(deps.store, failChatId),
                new Promise<never>((_, reject) =>
                    setTimeout(() => reject(new Error('store-probe-timeout')), STORE_PROBE_TIMEOUT_MS)),
            ])
        } catch (probeErr: unknown) {
            storeAtFailure = probeErr instanceof Error && probeErr.message === 'store-probe-timeout'
                ? ' | store-at-failure: lookup-timeout'
                : ' | store-at-failure: lookup-error'
        }
        // BE#211 false-FAIL recovery. An ambiguous failure (the engine's
        // send-confirmation timed out, or the send threw) is NOT proof the send
        // failed: WhatsApp may have accepted the message and the confirmation
        // simply arrived after the timeout. When the probe proves that — the
        // store holds the message at ack >= SERVER — recover it as a real
        // delivery using the key the probe already found, applying the SAME
        // ack-gate + success-write as the granted-path and lock-denied
        // store-check guards above. Otherwise a delivered send is durably
        // mislabeled `failed`: it renders as a "missed" reminder (BE#211) and
        // corrupts /v1, campaign counters, and deliverability stats. A stored
        // copy at ack < SERVER (PENDING/ERROR) never reached WhatsApp's server,
        // so it is left failed (retry-eligible) with the observability verdict.
        // FE#360 — Status posts clear this via storeHitProvesDelivery (tag hit, no
        // ack gate). This is the exact shape of the duplicate-Status bug: the post
        // was accepted, the confirmation was lost, and without recovery here the
        // message stays failed → re-dispatched → posted publicly a second time.
        const verdict = hit ? classifyStoreHit(hit, failChatId) : null
        if (hit && verdict === 'maybe-sent') {
            const logResponse = await recordMaybeSent(hit)
            log?.('info', `[send-protocol] ${userMessage._id} failed ambiguously but a post-wire copy exists (ack=${hit.ack}) — recorded pending-ack (#385)`)
            return { status: 'skipped_already_sent', waMessageKey: hit.waMessageKey, logResponse }
        }
        if (hit && verdict === 'delivered') {
            const logResponse = await backendClient.deleteUserMessageAndCreateLog({
                messageId: userMessage._id,
                waMessageKey: hit.waMessageKey,
                ack: hit.ack,
                sendFailureError: undefined,
                attemptType,
                whatsappStatus,
                logMessage: `${successLogMessage(attemptType, retryAttemptNumber)} — recovered after send-confirmation timeout (store-at-failure: present ack=${hit.ack})`,
                retryTrigger: input.retryTrigger,
                mqttClientId: engineContext.mqttClientId,
                clientType: engineContext.clientType,
                waVersion: engineContext.waVersion,
                waMeUser: engineContext.waMeUser,
                waState: input.waState,
                waStream: input.waStream,
                waHasSynced: input.waHasSynced,
            })
            log?.('info', `[send-protocol] recovered ${userMessage._id} as ${hit.waMessageKey} — send reported failure but store shows ack=${hit.ack} (BE#211 false-FAIL)`)
            return { status: 'skipped_already_sent', waMessageKey: hit.waMessageKey, logResponse }
        }
        if (!storeAtFailure) {
            storeAtFailure = hit
                ? ` | store-at-failure: present ack=${hit.ack}`
                : ' | store-at-failure: absent'
        }
    }
    // The engine-side thunk may have appended its own diagnostic suffix
    // (`| send-trace: …`) to the error string; keep the customer-visible
    // channels clean of ALL diagnostics.
    const cleanFailureError = stripDiagnosticSuffixes(failureError)
    const failureLogMessage = (threwMessage
        ? `${attemptType === 'initial' ? 'initial send' : `retry #${retryAttemptNumber}`} exception occurred`
        : `${attemptType === 'initial' ? 'Initial send' : `Retry #${retryAttemptNumber}`} failed with error response`)
        + storeAtFailure
    // Precedence: the engine's own record wins over `failureCode` (it knows the stage and whether bytes hit the wire);
    // status decisions use `failureCode`, never the record.
    // One record per failed log, same shape for every engine. A failure whose throw site built no record (a bare
    // throw, an engine not yet migrated) gets the conservative one: stage `send`, on-wire `unknown` (never resent blind).
    const failureRecord = failureToMetadata(engineFailure ?? sanitizeFailure({
        stage: 'send',
        engine: engineOfClientType(engineContext.clientType),
        code: failureCode,
        errorClass: threwMessage === undefined ? undefined : caughtError,
        retryAfterSec,
    }))
    let logResponse: Awaited<ReturnType<typeof backendClient.deleteUserMessageAndCreateLog>> | undefined
    try {
        logResponse = await backendClient.deleteUserMessageAndCreateLog({
            ...failureRecord,
            messageId: userMessage._id,
            waMessageKey: '',
            sendFailureError: cleanFailureError,
            attemptType,
            whatsappStatus,
            logMessage: failureLogMessage,
            retryTrigger: input.retryTrigger,
            mqttClientId: engineContext.mqttClientId,
            clientType: engineContext.clientType,
            waVersion: engineContext.waVersion,
            waMeUser: engineContext.waMeUser,
            waState: input.waState,
            waStream: input.waStream,
            waHasSynced: input.waHasSynced,
        })
    } catch (logErr: unknown) {
        const m = logErr instanceof Error ? logErr.message : 'unknown'
        log?.('warn', `[send-protocol] failure-log write failed: ${m}`)
    }
    return { status: 'failed', error: cleanFailureError, code: failureCode, logResponse }
}

/** The pre-send recipient gates (the engine's adapters): a recipient not on
 *  WhatsApp, or a send the account restriction would refuse, fails the attempt
 *  before the socket is touched. A gate-service error never blocks the send. */
async function recipientGate(
    deps: SendProtocolDeps,
    chatId: string | undefined,
    engine: SendFailEngine,
): Promise<Extract<SendFnResult, { ok: false }> | null> {
    if (!chatId) return null
    try {
        if (deps.validateRecipient && await deps.validateRecipient(chatId) === 'not_on_whatsapp') {
            return { ok: false, error: 'Recipient is not on WhatsApp', code: 'not_on_whatsapp', failure: sanitizeFailure({ stage: 'prep', engine, code: 'not_on_whatsapp' }) }
        }
        const risk = deps.sendRisk ? await deps.sendRisk(chatId) : undefined
        if (risk?.blocked) return { ok: false, error: risk.reason, code: 'account_restricted', failure: sanitizeFailure({ stage: 'prep', engine, code: 'account_restricted' }) }
    } catch (err: unknown) {
        deps.log?.('warn', `[send-protocol] recipient gate error (send proceeds): ${err instanceof Error ? err.message : String(err)}`)
    }
    return null
}
